import { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  ShieldCheck,
  AlertTriangle,
  Save,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import type { PetFormData, ParasitarioEntry } from "@/pages/CadastrarPet";

type ParasitarioEntryComDelete = ParasitarioEntry & {
  _delete?: number;
};

interface Props {
  formData: PetFormData;
  updateField: <K extends keyof PetFormData>(field: K, value: PetFormData[K]) => void;
}

const opcoesTipo = [
  { value: "antipulgas", label: "Antipulgas / Carrapatos" },
  { value: "vermifugo", label: "Vermífugo" },
];

function getPetIdDaUrl() {
  const match = window.location.pathname.match(/\/pets\/(\d+)\/(editar|edit)/);
  return match?.[1] || null;
}

function getLabelTipo(tipo: string) {
  const encontrado = opcoesTipo.find((item) => item.value === tipo);
  return encontrado?.label || tipo || "Não selecionado";
}

function getIntervaloDias(tipo: string) {
  if (tipo === "vermifugo") return 90;
  return 30;
}

function somarDias(data: string, dias: number) {
  if (!data || !dias) return "";

  const d = new Date(`${data}T00:00:00`);
  d.setDate(d.getDate() + dias);

  const ano = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");

  return `${ano}-${mes}-${dia}`;
}

function getStatus(data?: string) {
  if (!data) return { variant: "outline" as const, text: "Sem data" };

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const d = new Date(`${data}T00:00:00`);

  if (d < hoje) return { variant: "destructive" as const, text: "Atrasado" };

  const diff = Math.ceil((d.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 30) return { variant: "secondary" as const, text: "Próximo" };

  return { variant: "default" as const, text: "Em dia" };
}

export default function ControleParasitarios({ formData, updateField }: Props) {
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const [salvando, setSalvando] = useState(false);

  const petId = getPetIdDaUrl();
  const todosParasitas = (formData.parasitas || []) as ParasitarioEntryComDelete[];
  const parasitasVisiveis = todosParasitas.filter((item) => Number(item._delete ?? 0) !== 1);
  const vazio = parasitasVisiveis.length === 0;

  const toggleItem = (id: string) => {
    setAbertos((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const adicionarRegistro = () => {
    const novoId = crypto.randomUUID();

    const novo: ParasitarioEntry = {
      id: novoId,
      tipo: "vermifugo",
      produto: "",
      data_aplicacao: "",
      data_reaplicar: "",
      peso: "",
      dose: "",
      observacoes: "",
      validado: 0,
    };

    updateField("parasitas", [...todosParasitas, novo]);
    setAbertos((prev) => new Set(prev).add(novoId));
  };

  const removerRegistro = (id: string) => {
    const atualizadas = todosParasitas
      .map((item) => {
        if (item.id !== id) return item;

        const idNumerico = /^\d+$/.test(String(item.id));

        if (idNumerico) {
          return { ...item, _delete: 1 };
        }

        return null;
      })
      .filter(Boolean) as ParasitarioEntryComDelete[];

    updateField("parasitas", atualizadas as any);

    setAbertos((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const atualizarRegistro = (
    id: string,
    field: keyof ParasitarioEntry,
    value: string | number
  ) => {
    updateField(
      "parasitas",
      todosParasitas.map((item) => {
        if (item.id !== id) return item;

        const atualizado: ParasitarioEntry = { ...item, [field]: value };

        const tipoAtual = field === "tipo" ? String(value) : atualizado.tipo;
        const dataAplicacao =
          field === "data_aplicacao" ? String(value) : atualizado.data_aplicacao;

        if (dataAplicacao && tipoAtual) {
          atualizado.data_reaplicar = somarDias(
            dataAplicacao,
            getIntervaloDias(tipoAtual)
          );
        }

        return atualizado;
      })
    );
  };

  async function salvarSecaoParasitas() {
    try {
      if (!petId) {
        toast.error("Salve o pet primeiro para depois salvar os parasitários.");
        return;
      }

      setSalvando(true);

      const token = (
        document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
      )?.content;

      const payload = {
        parasitas: (formData.parasitas || []).map((p: any) => ({
          id: /^\d+$/.test(String(p.id)) ? Number(p.id) : null,
          tipo: p.tipo || null,
          produto: p.produto || "",
          data_aplicacao: p.data_aplicacao || null,
          data_reaplicar: p.data_reaplicar || null,
          peso: p.peso || "",
          dose: p.dose || "",
          observacoes: p.observacoes || "",
          _delete: Number(p._delete ?? 0) === 1 ? 1 : 0,
        })),
      };

      const response = await fetch(`/pets/${petId}/parasitas/salvar`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { "X-CSRF-TOKEN": token } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || data?.message || "Erro ao salvar parasitários.");
      }

      const retorno: ParasitarioEntry[] = Array.isArray(data?.parasitas)
        ? data.parasitas.map((p: any) => ({
            id: String(p.id ?? crypto.randomUUID()),
            tipo: p.tipo ?? "",
            produto: p.produto ?? "",
            data_aplicacao: p.data_aplicacao ?? "",
            data_reaplicar: p.data_reaplicar ?? "",
            peso: p.peso != null ? String(p.peso) : "",
            dose: p.dose ?? "",
            observacoes: p.observacoes ?? "",
            validado: Number(p.validado ?? 0),
          }))
        : parasitasVisiveis;

      updateField("parasitas", retorno);
      toast.success("Parasitários salvos com sucesso.");
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || "Erro ao salvar parasitários.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="gradient-primary px-6 py-5 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-primary-foreground flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            Controle Parasitário
          </h2>
          <p className="text-primary-foreground/80 text-sm mt-1">
            Informe a data de aplicação. A próxima aplicação é calculada automaticamente.
          </p>
        </div>

        <Button
          type="button"
          size="sm"
          onClick={adicionarRegistro}
          className="bg-card/20 hover:bg-card/30 text-primary-foreground border border-primary-foreground/30 gap-2 font-semibold"
        >
          <Plus className="w-4 h-4" />
          Adicionar
        </Button>
      </div>

      <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border-b border-amber-200">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          Registros preenchidos pelo tutor podem precisar de conferência posterior.
        </p>
      </div>

      <div className="p-4">
        {vazio ? (
          <div className="text-center py-10 text-muted-foreground">
            <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Nenhum controle parasitário cadastrado.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {parasitasVisiveis.map((item, index) => {
              const aberto = abertos.has(item.id);
              const status = getStatus(item.data_reaplicar);
              const validado = Number(item.validado) === 1;

              const temDadosCompletos = Boolean(
                item.tipo &&
                  item.produto?.trim() &&
                  item.data_aplicacao
              );

              const statusFinal = validado
                ? "validado"
                : temDadosCompletos
                ? "preenchido"
                : "incompleto";

              return (
                <div
                  key={item.id}
                  className="border border-border rounded-xl overflow-hidden bg-white shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => toggleItem(item.id)}
                    className="w-full px-4 py-4 bg-white hover:bg-muted/30 transition-colors border-b"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-left">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-bold text-foreground text-base">
                            {getLabelTipo(item.tipo)}
                          </span>
                          <Badge variant={status.variant}>{status.text}</Badge>
                        </div>

                        <p className="text-xs text-muted-foreground mt-1">
                          Registro {index + 1}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-sm text-blue-600">Ver/Editar</span>
                        {aberto ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </button>

                  {aberto && (
                    <div className="p-4 space-y-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h4 className="font-semibold text-foreground">
                            {getLabelTipo(item.tipo)}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Registro {index + 1}
                          </p>
                        </div>

                        <div
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                            statusFinal === "validado"
                              ? "bg-green-100 text-green-700"
                              : statusFinal === "preenchido"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {statusFinal === "validado"
                            ? "Validado"
                            : statusFinal === "preenchido"
                            ? "Preenchido"
                            : "Dados incompletos"}
                        </div>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Tipo
                          </label>
                          <select
                            value={item.tipo}
                            onChange={(e) =>
                              atualizarRegistro(item.id, "tipo", e.target.value)
                            }
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            disabled={validado}
                          >
                            <option value="">Selecione</option>
                            {opcoesTipo.map((opcao) => (
                              <option key={opcao.value} value={opcao.value}>
                                {opcao.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Produto
                          </label>
                          <Input
                            value={item.produto || ""}
                            onChange={(e) =>
                              atualizarRegistro(item.id, "produto", e.target.value)
                            }
                            placeholder="Ex: NexGard / Drontal"
                            disabled={validado}
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Aplicação
                          </label>
                          <Input
                            type="date"
                            value={item.data_aplicacao}
                            onChange={(e) =>
                              atualizarRegistro(item.id, "data_aplicacao", e.target.value)
                            }
                            disabled={validado}
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Data prevista
                          </label>
                          <Input
                            type="date"
                            value={item.data_reaplicar}
                            onChange={(e) =>
                              atualizarRegistro(item.id, "data_reaplicar", e.target.value)
                            }
                            disabled={validado}
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Peso (kg)
                          </label>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.peso || ""}
                            onChange={(e) =>
                              atualizarRegistro(item.id, "peso", e.target.value)
                            }
                            placeholder="Ex: 12.50"
                            disabled={validado}
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Dose
                          </label>
                          <Input
                            value={item.dose || ""}
                            onChange={(e) =>
                              atualizarRegistro(item.id, "dose", e.target.value)
                            }
                            placeholder="Ex: 1 comprimido"
                            disabled={validado}
                          />
                        </div>

                        <div className="md:col-span-2 lg:col-span-4">
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Observações
                          </label>
                          <Input
                            value={item.observacoes || ""}
                            onChange={(e) =>
                              atualizarRegistro(item.id, "observacoes", e.target.value)
                            }
                            placeholder="Observações"
                            disabled={validado}
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removerRegistro(item.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          disabled={validado}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button type="button" onClick={salvarSecaoParasitas} disabled={salvando}>
            <Save className="w-4 h-4 mr-2" />
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>
    </div>
  );
}